import { AnimatePresence, m } from 'framer-motion'
import type { MotionValue } from 'framer-motion'
import * as React from 'react'
import {
  AIDA_ANIMATED_MOUTH_PATH,
  AIDA_AURORA_STOPS,
  AIDA_EYE_HEIGHT,
  AIDA_EYE_RX,
  AIDA_EYE_WIDTH,
  AIDA_EYE_Y,
} from './aida-icon-animated-paths'

const EYE_BLINK_STYLE = { transformBox: 'fill-box', originX: '50%', originY: '50%' } as const
const MOUTH_MOTION_STYLE = { transformBox: 'fill-box', originX: '50%', originY: '0%' } as const
const MOUTH_ENTER = { opacity: 0, scaleY: 0.6 }
const MOUTH_SHOW = { opacity: 1, scaleY: 1 }
const MOUTH_EXIT = { opacity: 0, scaleY: 0.6 }
const MOUTH_TRANSITION = { duration: 0.18, ease: 'easeOut' as const }
const MOUTH_INSTANT = { duration: 0 }

export function auroraGradientFill(gradientId: string) {
  return `url(#${gradientId})`
}

export function AuroraFill({
  svgId,
  gradientId,
  animate: shouldAnimate,
}: {
  svgId: string
  gradientId: string
  animate: boolean
}) {
  return (
    <>
      <style>{`#${svgId} path,#${svgId} rect{fill:url(#${gradientId})}`}</style>
      <defs>
        <linearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1="0"
          x2="20"
          y1="10"
          y2="10"
        >
          {AIDA_AURORA_STOPS.map((color, index) => (
            <stop
              key={`${color}-${index}`}
              offset={`${(index / (AIDA_AURORA_STOPS.length - 1)) * 100}%`}
              stopColor={color}
            />
          ))}
          {shouldAnimate ? (
            <>
              <animate attributeName="x1" values="0;-20" dur="10s" repeatCount="indefinite" />
              <animate attributeName="x2" values="20;0" dur="10s" repeatCount="indefinite" />
            </>
          ) : null}
        </linearGradient>
      </defs>
    </>
  )
}

export function AnimatedEye({
  x,
  lidScale,
  fill,
}: {
  x: MotionValue<number>
  lidScale: MotionValue<number>
  fill: string
}) {
  return (
    <m.rect
      x={x}
      y={AIDA_EYE_Y}
      width={AIDA_EYE_WIDTH}
      height={AIDA_EYE_HEIGHT}
      rx={AIDA_EYE_RX}
      fill={fill}
      style={{ ...EYE_BLINK_STYLE, scaleY: lidScale }}
    />
  )
}

export function GazeGroup({
  x,
  y,
  children,
}: {
  x: MotionValue<number>
  y: MotionValue<number>
  children: React.ReactNode
}) {
  const ref = React.useRef<SVGGElement>(null)

  React.useEffect(() => {
    const node = ref.current
    if (!node) {
      return
    }
    const write = () => {
      node.setAttribute('transform', `translate(${x.get()} ${y.get()})`)
    }
    write()
    const stopX = x.on('change', write)
    const stopY = y.on('change', write)
    return () => {
      stopX()
      stopY()
    }
  }, [x, y])

  return <g ref={ref}>{children}</g>
}

export function AnimatedMouth({
  visible,
  animate: shouldAnimate,
}: {
  visible: boolean
  animate: boolean
}) {
  return (
    <AnimatePresence>
      {visible ? (
        <m.path
          d={AIDA_ANIMATED_MOUTH_PATH}
          fill="currentColor"
          initial={shouldAnimate ? MOUTH_ENTER : false}
          animate={MOUTH_SHOW}
          exit={MOUTH_EXIT}
          transition={shouldAnimate ? MOUTH_TRANSITION : MOUTH_INSTANT}
          style={MOUTH_MOTION_STYLE}
        />
      ) : null}
    </AnimatePresence>
  )
}
